/**
 * Export Dialog Component
 *
 * Dialog for exporting IDF files with format options.
 */

import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { useFileStore } from '../index';
import type { ExportOptions } from '../types';

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExportDialog({ open, onOpenChange }: ExportDialogProps) {
  const { exportOptions, setExportOptions, exportDocument, currentFileName } = useFileStore();
  const [filename, setFilename] = useState(() => {
    const name = currentFileName ?? 'model';
    return name.replace(/\.(idf|imf)$/i, '');
  });

  const handleExport = useCallback(() => {
    exportDocument(filename);
    onOpenChange(false);
  }, [exportDocument, filename, onOpenChange]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const updateOption = useCallback(
    <K extends keyof ExportOptions>(key: K, value: ExportOptions[K]) => {
      setExportOptions({ [key]: value });
    },
    [setExportOptions]
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg border bg-background p-6 shadow-lg">
        <div className="flex items-center justify-between border-b pb-4">
          <h2 className="text-lg font-semibold">Export IDF File</h2>
          <button
            onClick={handleCancel}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="space-y-4 py-4">
          {/* Filename */}
          <div>
            <label className="mb-1 block text-sm font-medium">Filename</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={filename}
                onChange={(e) => { setFilename(e.target.value); }}
                className="flex-1 rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="model"
              />
              <span className="text-sm text-muted-foreground">
                .{exportOptions.format}
              </span>
            </div>
          </div>

          {/* Format */}
          <div>
            <label className="mb-1 block text-sm font-medium">Format</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="format"
                  checked={exportOptions.format === 'idf'}
                  onChange={() => { updateOption('format', 'idf'); }}
                  className="h-4 w-4"
                />
                <span className="text-sm">IDF (Standard)</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="format"
                  checked={exportOptions.format === 'imf'}
                  onChange={() => { updateOption('format', 'imf'); }}
                  className="h-4 w-4"
                />
                <span className="text-sm">IMF (Macro-enabled)</span>
              </label>
            </div>
          </div>

          {/* Line endings */}
          <div>
            <label className="mb-1 block text-sm font-medium">Line Endings</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="lineEndings"
                  checked={exportOptions.lineEndings === 'unix'}
                  onChange={() => { updateOption('lineEndings', 'unix'); }}
                  className="h-4 w-4"
                />
                <span className="text-sm">Unix (LF)</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="lineEndings"
                  checked={exportOptions.lineEndings === 'windows'}
                  onChange={() => { updateOption('lineEndings', 'windows'); }}
                  className="h-4 w-4"
                />
                <span className="text-sm">Windows (CRLF)</span>
              </label>
            </div>
          </div>

          {/* Options */}
          <div className="space-y-2">
            <label className="mb-1 block text-sm font-medium">Options</label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={exportOptions.sortObjects}
                onChange={(e) => { updateOption('sortObjects', e.target.checked); }}
                className="h-4 w-4 rounded"
              />
              <span className="text-sm">Sort objects by class name</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={exportOptions.includeComments}
                onChange={(e) => { updateOption('includeComments', e.target.checked); }}
                className="h-4 w-4 rounded"
              />
              <span className="text-sm">Include comments</span>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={!filename.trim()}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mr-2"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export
          </Button>
        </div>
      </div>
    </div>
  );
}
